import * as $ from 'jquery';

import * as CanvasTemplates from 'src/integration/canvas/templates';
import * as CanvasUiUtil from 'src/integration/canvas/ui/util';
import { canManageFilesTool } from 'src/integration/canvas/ui/util';
import * as AllyIntegrationUtil from 'src/integration/util';
import * as AllyConfig from 'src/shared/rest/Config';
import { FileReference } from 'src/shared/rest/domain/FileReference';

// Observe changes to the files tool preview container
var filesToolPreviewObserver = null;
var filePreviewId = null;

export function pollPreview(previewActivePrecondition, previewIdLocator) {
    var $previewOverlay = $('#file-preview-modal-alert').parent().parent();
    if ($previewOverlay.length) {
        rebindFilesToolPreviewObserver(
            $previewOverlay,
            (filePreviewId === null),
            previewActivePrecondition,
            previewIdLocator
        );
    } else {
        filePreviewId = null;
        unbindFilesToolPreviewObserver();
    }
}

/**
 * Bind or rebind the files tool preview observer. The observer takes care of updating the state of ally
 * enhancements whenever the iframe showing a preview changes from one to another. This helps be as quick as
 * possible when a new file is switched to, so we can take quick measures to guard seizures.
 *
 * @param  {jQuery}     $previewOverlay     The preview overlay element that holds the preview frame
 * @param  {Boolean}    force               Forces the observer to be rebound even if there
 *                                          is already one bound for the current frame on the DOM
 */
function rebindFilesToolPreviewObserver($previewOverlay, force, previewActivePrecondition, previewIdLocator) {
    var $previewFrame = $previewOverlay.find('#file-preview-iframe').first();

    if (!$previewFrame.length) {
        // If the preview frame is not found, we cannot enhance it
        return;
    }

    // Ensure the asynchronous call to load the files has completed before we try and enhance the preview container
    // based on accessibility metadata
    var filesLoaded = (Object.keys(CanvasUiUtil.fileInformation()).length > 0);

    if (!filesLoaded && previewActivePrecondition($previewFrame)) {
        // Always defer binding the updater until we have file information available
        return;
    } else if (!force && filesToolPreviewObserver && filesToolPreviewObserver.el === $previewOverlay[0]) {
        // Only rebind an observer if we are "force"d to, or if one is not bound yet. When the observer element
        // exists but doesn't equal the preview overlay element at hand, it indicates the frame was closed
        // (removed from the dom) and then recreated
        return;
    }

    // Ensure we clear any existing observer
    unbindFilesToolPreviewObserver();
    filesToolPreviewObserver = {
        'el': $previewOverlay[0],
        'observer': new MutationObserver(function () {
            // refresh preview frame - the old one may have been removed from the DOM
            $previewFrame = $previewOverlay.find('#file-preview-iframe').first();
            updateFilesToolPreviewEnhancement($previewOverlay, $previewFrame, previewIdLocator);
        })
    };

    // Since we know the iframe should be displaying its first file, apply any applicable enhancements
    updateFilesToolPreviewEnhancement($previewOverlay, $previewFrame, previewIdLocator);

    // Begin observing changes in the iframe src
    filesToolPreviewObserver.observer.observe($previewOverlay[0], {
        'attributes': true
    });
}

/**
 * Update the files tool preview enhancements if anything has changed from its current state.
 *
 * @param  {jQuery}     $previewOverlay     The overlay element that contains the whole preview pane
 * @param $previewFrame
 * @param previewIdLocator
 */
// Main function
function updateFilesToolPreviewEnhancement($previewOverlay, $previewFrame, previewIdLocator) {
    const currentFilePreviewId = previewIdLocator($previewOverlay);
    if (filePreviewId === currentFilePreviewId) return;

    const currentFilePreviewReference = new FileReference(currentFilePreviewId);
    const fileInfo = CanvasUiUtil.fileInformation()[currentFilePreviewReference.id()];
    if (!fileInfo) return;

    filePreviewId = currentFilePreviewId;
    $previewOverlay.find('.ally-enhancement-preview').remove();

    if (fileInfo.seizureRisk) {
        handleSeizureRisk($previewOverlay, $previewFrame, fileInfo, currentFilePreviewReference);
        return;
    }

    if (removeSeizureGuardIfPresent($previewOverlay, $previewFrame)) return;

    showFrameOnLoad($previewOverlay, $previewFrame);
}

// Handles the seizure risk scenario
function handleSeizureRisk($previewOverlay, $previewFrame, fileInfo, fileReference) {
    $previewFrame.hide();
    findAllySeizureGuard($previewOverlay).remove();

    let $seizureGuard = $(CanvasTemplates.apply('ally-files-preview-seizure', {
        'ALLY_DOMAIN': AllyConfig.baseUrl(),
        'canFix': AllyIntegrationUtil.hasScore(fileInfo) && canManageFilesTool()
    }));

    $seizureGuard.find('button.ally-files-preview-seizure-show-me').click(function () {
        $seizureGuard.remove();
        $previewFrame.show();
        $seizureGuard = null;
        return false;
    });

    $seizureGuard.find('button.ally-files-preview-seizure-fix-me').click(function () {
        handleFixMeClick($(this), fileInfo, fileReference);
        return false;
    });

    $previewFrame.after($seizureGuard);
}

// Handles the "Fix Me" button click
function handleFixMeClick($trigger, fileInfo, fileReference) {
    var callbacks = {
        'deleted': function () {
            window.location.assign(window.location.href.replace(/preview=\d+/, ''));
        },
        'closed': function () {
            $trigger.focus();
        }
    };

    const $parent = $trigger.parents().eq(3);
    if (!$parent.length) {
        // If we cannot find the parent, we cannot append the Ally integration UI
        console.warn('Could not find parent element to append Ally integration UI.');
        return;
    }
    const classAttr = $parent.attr('class');
    const appendTo = classAttr
        ? `.${classAttr.trim().split(/\s+/).join('.')}`
        : null;

    var opts = { 'filePreviewUrl': CanvasUiUtil.getPreviewUrl(fileInfo), appendTo };
    AllyIntegrationUtil.launchInstructorFeedback(
        AllyConfig.courseId(),
        fileReference,
        opts,
        callbacks
    );
}

// Removes seizure guard if present and shows the frame
function removeSeizureGuardIfPresent($previewOverlay, $previewFrame) {
    if ($previewOverlay.find('.ally-files-preview-seizure').length) {
        findAllySeizureGuard($previewOverlay).remove();
        $previewFrame.show();
        return true;
    }
    return false;
}

// Ensures the frame is shown after load if no seizure risk
function showFrameOnLoad($previewOverlay, $previewFrame) {
    $previewFrame.one('load', function () {
        findAllySeizureGuard($previewOverlay).remove();
        $previewFrame.show();
    });
}

/**
 * Unbind the files tool preview observer from the DOM such that it will no longer listen for events and can be
 * later re-bound.
 */
function unbindFilesToolPreviewObserver() {
    if (filesToolPreviewObserver) {
        filesToolPreviewObserver.observer.disconnect();
        filesToolPreviewObserver = null;
    }
}

function findAllySeizureGuard($previewOverlay) {
    return $previewOverlay.find('.ally-files-preview-seizure');
}
